/**
 * Marker op de levenstijdlijn (spec momenten stap 5): een bewaard moment
 * (stip), een range (balkje) of een berekende cyclus (systeem-marker).
 * De pagina bouwt deze uit de hasMoment-/hasCycle-links en de cycli-service;
 * de component tekent alleen.
 */
export interface HdLifeTimelineMarker {
  readonly id: string;
  readonly kind: 'moment' | 'range' | 'cycle';
  readonly startUtc: string;
  /** Alleen bij een range: einde van de periode. */
  readonly endUtc?: string | null;
  readonly label: string;
  /** Kale FA-naam (custom chart-icoon wint, zoals in de Momenten-sectie). */
  readonly icon: string;
  readonly hasNote?: boolean;
  readonly attachmentCount?: number;
  /** Doorklikbare chart; berekende cycli zonder opgeslagen chart hebben er geen. */
  readonly openChartId?: string | null;
  /**
   * I Tjing-moment (Classic I Ching): de raadpleging uit het dagboek die hier
   * staat. Openen gaat dan naar de I Tjing-momentdialoog, niet naar een chart.
   */
  readonly consultationId?: string;
  // Momentdetails (spec docs/LEVENSTIJDLIJN.md §2.10), alle optioneel.
  /** Momentkleur als hex (preset-key al opgelost); null = default-stijl. */
  readonly colorHex?: string | null;
  /** Categorie-key (daily/lifetime); het categoriefilter werkt hierop. */
  readonly category?: string | null;
  /** Stemming 1..5 (kleurmodus 'mood' kleurt de stip/balk hiermee). */
  readonly mood?: number | null;
  /** Vitaliteit 1..5 (popover-indicator). */
  readonly vitality?: number | null;
  /** Emotie-keys (popover). */
  readonly emotions?: readonly string[];
}

export type HdLifeTimelineZoom = 'decade' | 'fiveYears' | 'year' | 'month';

/** Zichtbaar tijdvenster van de tijdlijn; de pagina laadt hierop transit-segmenten (spec §2.5). */
export interface HdLifeTimelineViewport {
  readonly fromMs: number;
  readonly toMs: number;
  readonly zoom: HdLifeTimelineZoom;
}

/** Eén poort-blok in de transit-strip (stuksgewijs constant tussen twee wissels). */
export interface HdLifeTransitBlock {
  readonly fromMs: number;
  readonly toMs: number;
  readonly gate: number;
}

/** Eén planeet-rij in de transit-strip onder de momentenrijen (spec §2.5). */
export interface HdLifeTransitLane {
  readonly planetKey: string;
  readonly label: string;
  readonly color: string;
  readonly blocks: readonly HdLifeTransitBlock[];
}

export const HD_LIFE_TIMELINE_ZOOMS: readonly HdLifeTimelineZoom[] = ['decade', 'fiveYears', 'year', 'month'];

/** Pixels per jaar per zoomniveau — het hele viewport-model is lineair px-per-ms. */
export const HD_LIFE_TIMELINE_PX_PER_YEAR: Readonly<Record<HdLifeTimelineZoom, number>> = {
  decade: 30,
  fiveYears: 72,
  year: 240,
  month: 1440,
};
